/**
 * ImportDataPage - Universal Import with Tokyo Night Premium Theme
 *
 * A stunning, immersive import experience featuring:
 * - Animated step indicators with glowing orbs
 * - Pulsing dropzone with gradient borders
 * - Smooth micro-interactions throughout
 * - Celebration animation on success
 *
 * Supports: OFX, QFX, QIF, CSV (with smart detection), JSON
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { Card, CardHeader, Button } from '@/components/shared';
import { useAccounts } from '@/hooks/useAccounts';
import {
  parseFileForPreview,
  importTransactions,
  importNinjaCashBackup,
  ImportPreview,
  UniversalImportResult,
  ColumnMapping,
  BANK_PRESETS,
  getBankPresetsByCountry,
} from '@/services/universalImport';
import { importOneMoneyCSV, ImportResult as OneMoneyImportResult } from '@/services/import';
import { trackImportCompleted, trackImportFailed, type ImportMetricSummary } from '@/services/metrics';
import { formatCurrency as formatCurrencyIntl, formatDate as formatDateIntl } from '@/utils/format';
import { useLanguage, t as i18nT } from '@/i18n';
import './ImportDataPage.css';

// ============================================================
// Icons - Premium SVG Icons
// ============================================================

const UploadIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M52 40v12a4 4 0 0 1-4 4H16a4 4 0 0 1-4-4V40" />
    <path d="M44 20L32 8L20 20" />
    <path d="M32 8v32" />
    <circle cx="32" cy="32" r="28" strokeDasharray="4 4" opacity="0.3" />
  </svg>
);

const FileAcceptIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="32" cy="32" r="24" />
    <path d="M24 32l6 6 12-12" />
    <circle cx="32" cy="32" r="28" strokeDasharray="4 4" opacity="0.3" />
  </svg>
);

const FileRejectIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="32" cy="32" r="24" />
    <path d="M24 24l16 16" />
    <path d="M40 24l-16 16" />
  </svg>
);

const SuccessIcon = () => (
  <svg width="80" height="80" viewBox="0 0 80 80" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="40" cy="40" r="36" />
    <path d="M26 40l10 10 20-20" />
    <circle cx="40" cy="40" r="38" strokeDasharray="6 6" opacity="0.3" />
  </svg>
);

const ErrorIcon = () => (
  <svg width="80" height="80" viewBox="0 0 80 80" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="40" cy="40" r="36" />
    <path d="M28 28l24 24" />
    <path d="M52 28l-24 24" />
  </svg>
);

// ============================================================
// Step Components
// ============================================================

type ImportStep = 'upload' | 'preview' | 'mapping' | 'confirm' | 'result';
type ImportFailureStage = 'file_rejected' | 'file_size' | 'parse_preview' | 'preview_validation' | 'confirm_import';

const IMPORT_ISSUES_URL = 'https://github.com/TakashiKyoto/ninjacash/issues';

function createFailedImportResult(errors: string[], warnings: string[] = []): UniversalImportResult {
  return {
    success: false,
    imported: 0,
    skipped: 0,
    duplicatesSkipped: 0,
    errors,
    warnings,
    newAccounts: [],
    newCategories: [],
  };
}

function classifyImportFailure(errors: string[]): string {
  const text = errors.join(' ').toLowerCase();
  if (text.includes('too large') || text.includes('maximum size')) return 'file_too_large';
  if (text.includes('unsupported') || text.includes('file type')) return 'unsupported_format';
  if (text.includes('account selected') || text.includes('select an account')) return 'account_required';
  if (text.includes('target account not found')) return 'account_missing';
  if (text.includes('column') || text.includes('mapping') || text.includes('parse')) return 'parse_or_mapping';
  if (text.includes('no transaction')) return 'no_transactions_found';
  return 'import_failed';
}

function buildImportFailureSummary(params: {
  result: UniversalImportResult;
  format?: string;
  stage: ImportFailureStage;
  fileSize?: number;
}): ImportMetricSummary {
  return {
    success: false,
    imported: params.result.imported,
    skipped: params.result.skipped,
    duplicatesSkipped: params.result.duplicatesSkipped,
    newAccounts: params.result.newAccounts.length,
    newCategories: params.result.newCategories.length,
    errorCount: params.result.errors.length,
    warningCount: params.result.warnings.length,
    format: params.format || 'unknown',
    stage: params.stage,
    fileSizeKb: params.fileSize ? Math.ceil(params.fileSize / 1024) : undefined,
    failureCode: classifyImportFailure(params.result.errors),
  };
}

interface StepIndicatorProps {
  currentStep: ImportStep;
  format: string | null;
}

function StepIndicator({ currentStep, format }: StepIndicatorProps) {
  const { t } = useLanguage();
  const steps = [
    { id: 'upload', label: t('import_page.steps.upload') },
    { id: 'preview', label: t('import_page.steps.preview') },
    ...(format === 'csv' ? [{ id: 'mapping', label: t('import_page.steps.mapping') }] : []),
    { id: 'confirm', label: t('import_page.steps.confirm') },
    { id: 'result', label: t('import_page.steps.done') },
  ];

  const currentIndex = steps.findIndex(s => s.id === currentStep);

  return (
    <div className="step-indicator">
      {steps.map((step, index) => (
        <div
          key={step.id}
          className={`step ${index < currentIndex ? 'completed' : ''} ${index === currentIndex ? 'active' : ''}`}
        >
          <div className="step-number">
            {index < currentIndex ? '' : index + 1}
          </div>
          <div className="step-label">{step.label}</div>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// File Upload Step
// ============================================================

interface FileUploadStepProps {
  onFileSelect: (file: File) => void;
  onFileReject: (rejection: FileRejection) => void;
  isLoading: boolean;
}

function FileUploadStep({ onFileSelect, onFileReject, isLoading }: FileUploadStepProps) {
  const { t } = useLanguage();
  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
    if (rejectedFiles.length > 0) {
      console.warn('[Import] Rejected files:', rejectedFiles);
      onFileReject(rejectedFiles[0]);
      return;
    }
    if (acceptedFiles.length > 0) {
      onFileSelect(acceptedFiles[0]);
    }
  }, [onFileSelect, onFileReject]);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: {
      'application/x-ofx': ['.ofx'],
      'application/x-qfx': ['.qfx'],
      'application/qif': ['.qif'],
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv'],
      'application/json': ['.json'],
      'text/plain': ['.ofx', '.qfx', '.qif', '.csv'],
    },
    multiple: false,
    disabled: isLoading,
  });

  const dropzoneClass = useMemo(() => {
    let className = 'dropzone';
    if (isDragActive) className += ' active';
    if (isDragAccept) className += ' accept';
    if (isDragReject) className += ' reject';
    if (isLoading) className += ' loading';
    return className;
  }, [isDragActive, isDragAccept, isDragReject, isLoading]);

  return (
    <div className="upload-step">
      <Card className="upload-card">
        <CardHeader
          title={t('settings.import.title')}
          subtitle={t('import_page.upload.subtitle')}
        />

        <div {...getRootProps({ className: dropzoneClass })}>
          <input {...getInputProps()} />

          {isLoading ? (
            <div className="dropzone-content">
              <div className="dropzone-icon loading-spinner" />
              <p className="dropzone-text">{t('import_page.upload.analyzing')}</p>
              <p className="dropzone-hint">{t('import_page.upload.analyzing_hint')}</p>
            </div>
          ) : isDragReject ? (
            <div className="dropzone-content reject">
              <div className="dropzone-icon">
                <FileRejectIcon />
              </div>
              <p className="dropzone-text">{t('import_page.upload.reject')}</p>
              <p className="dropzone-hint">{t('import_page.upload.reject_hint')}</p>
            </div>
          ) : isDragActive ? (
            <div className="dropzone-content accept">
              <div className="dropzone-icon">
                <FileAcceptIcon />
              </div>
              <p className="dropzone-text">{t('import_page.upload.drop_here')}</p>
            </div>
          ) : (
            <div className="dropzone-content">
              <div className="dropzone-icon">
                <UploadIcon />
              </div>
              <p className="dropzone-text">
                {t('import_page.upload.drop_or')} <span className="link">{t('import_page.upload.browse')}</span>
              </p>
              <p className="dropzone-hint">
                {t('import_page.upload.formats_hint')}
              </p>
            </div>
          )}
        </div>

        <div className="supported-formats">
          <h4>{t('import_page.upload.supported')}</h4>
          <div className="format-grid">
            <div className="format-item">
              <span className="format-badge ofx">OFX/QFX</span>
              <span className="format-desc">{t('import_page.upload.fmt_bank')}</span>
            </div>
            <div className="format-item">
              <span className="format-badge qif">QIF</span>
              <span className="format-desc">{t('import_page.upload.fmt_quicken')}</span>
            </div>
            <div className="format-item">
              <span className="format-badge csv">CSV</span>
              <span className="format-desc">{t('import_page.upload.fmt_csv')}</span>
            </div>
            <div className="format-item">
              <span className="format-badge json">JSON</span>
              <span className="format-desc">{t('import_page.upload.fmt_json')}</span>
            </div>
          </div>
        </div>
      </Card>

      <Card className="bank-presets-card">
        <CardHeader
          title={t('import_page.upload.banks_title')}
          subtitle={t('import_page.upload.banks_subtitle')}
        />
        <div className="bank-presets-grid">
          {Array.from(getBankPresetsByCountry().entries()).map(([country, presets]) => (
            <div key={country} className="bank-country">
              <h5 className="country-label">{country}</h5>
              <div className="banks-list">
                {presets.map(preset => (
                  <span key={preset.id} className="bank-tag">{preset.name}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ============================================================
// Preview Step
// ============================================================

interface PreviewStepProps {
  preview: ImportPreview;
  selectedAccountId: string;
  onAccountChange: (accountId: string) => void;
  onBack: () => void;
  onNext: () => void;
  onConfirmImport: () => void;
  accounts: Array<{ id: string; name: string; fullName?: string }>;
  isLoading: boolean;
}

function PreviewStep({
  preview,
  selectedAccountId,
  onAccountChange,
  onBack,
  onNext,
  onConfirmImport,
  accounts,
  isLoading,
}: PreviewStepProps) {
  const { t } = useLanguage();
  // Central locale-aware formatters (utils/format.ts) - never inline Intl here
  const formatDate = (date: Date) => {
    return formatDateIntl(date, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatAmount = (amount: number, type: string) => {
    const formatted = formatCurrencyIntl(amount, 'USD');
    return type === 'expense' ? `-${formatted}` : `+${formatted}`;
  };

  return (
    <div className="preview-step">
      <Card className="preview-summary-card">
        <CardHeader
          title="Import Preview"
          subtitle={`${preview.fileName} (${(preview.fileSize / 1024).toFixed(1)} KB)`}
        />

        <div className="preview-stats">
          <div className="stat">
            <span className="stat-value">{preview.totalTransactions}</span>
            <span className="stat-label">Total Found</span>
          </div>
          <div className="stat new">
            <span className="stat-value">{preview.newCount}</span>
            <span className="stat-label">New</span>
          </div>
          <div className="stat duplicate">
            <span className="stat-value">{preview.duplicateCount}</span>
            <span className="stat-label">Duplicates</span>
          </div>
        </div>

        {preview.bankPreset && (
          <div className="detected-preset">
            <span className="preset-label">Detected:</span>
            <span className="preset-value">{preview.bankPreset.name}</span>
          </div>
        )}

        <div className="format-badge-large">
          <span className={`format-badge ${preview.format}`}>{preview.format.toUpperCase()}</span>
        </div>
      </Card>

      {/* Show account selection only for non-backup imports */}
      {!preview.isNinjaCashBackup && (
        <Card className="account-select-card">
          <CardHeader
            title={t('import_page.preview.dest_title')}
            subtitle={t('import_page.preview.dest_subtitle')}
          />
          <select
            className="account-select"
            value={selectedAccountId}
            onChange={(e) => onAccountChange(e.target.value)}
          >
            <option value="">{t('import_page.preview.select_account')}</option>
            {accounts.map(acc => (
              <option key={acc.id} value={acc.id}>
                {acc.fullName || acc.name}
              </option>
            ))}
          </select>
        </Card>
      )}

      {/* Show backup restore info for NinjaCash backups */}
      {preview.isNinjaCashBackup && preview.backupData && (
        <Card className="backup-info-card">
          <CardHeader
            title={t('import_page.preview.backup_title')}
            subtitle={t('import_page.preview.backup_subtitle')}
          />
          <div className="backup-stats">
            <div className="backup-stat">
              <span className="backup-stat-value">{preview.backupData.accounts?.length || 0}</span>
              <span className="backup-stat-label">Accounts</span>
            </div>
            <div className="backup-stat">
              <span className="backup-stat-value">{preview.backupData.categories?.length || 0}</span>
              <span className="backup-stat-label">Categories</span>
            </div>
            <div className="backup-stat">
              <span className="backup-stat-value">{preview.backupData.transactions?.filter(t => !t.deletedAt).length || 0}</span>
              <span className="backup-stat-label">Transactions</span>
            </div>
          </div>
          <p className="backup-note">
            {t('import_page.preview.backup_note')}
          </p>
        </Card>
      )}

      {/* Show 1Money-specific info */}
      {preview.is1MoneyFormat && (
        <Card className="onemoney-info-card">
          <CardHeader
            title={t('import_page.preview.onemoney_title')}
            subtitle={t('import_page.preview.onemoney_subtitle')}
          />
          <div className="onemoney-features">
            <div className="onemoney-feature">
              <span className="feature-icon">✓</span>
              <span className="feature-text">Auto-creates accounts from FROM ACCOUNT column</span>
            </div>
            <div className="onemoney-feature">
              <span className="feature-icon">✓</span>
              <span className="feature-text">Preserves transfers between accounts</span>
            </div>
            <div className="onemoney-feature">
              <span className="feature-icon">✓</span>
              <span className="feature-text">Imports footer balances (if present)</span>
            </div>
            <div className="onemoney-feature">
              <span className="feature-icon">✓</span>
              <span className="feature-text">Detects recurring transaction patterns</span>
            </div>
            <div className="onemoney-feature">
              <span className="feature-icon">✓</span>
              <span className="feature-text">Maps account nicknames (C→Chase, Rev→Revolut)</span>
            </div>
          </div>
          <p className="onemoney-note">
            {t('import_page.preview.onemoney_note')}
          </p>
        </Card>
      )}

      <Card className="preview-table-card">
        <CardHeader
          title={t('import_page.preview.table_title')}
          subtitle={t('import_page.preview.table_subtitle', {
            shown: Math.min(10, preview.transactions.length),
            total: preview.totalTransactions,
          })}
        />

        <div className="preview-table-wrapper">
          <table className="preview-table">
            <thead>
              <tr>
                <th>{t('transactions.detail.date')}</th>
                <th>{t('transactions.detail.description')}</th>
                <th className="amount-col">{t('transactions.detail.amount')}</th>
                <th>{t('import_page.preview.status')}</th>
              </tr>
            </thead>
            <tbody>
              {preview.transactions.slice(0, 10).map((tx, index) => (
                <tr
                  key={index}
                  className={preview.duplicateFingerprints.has(tx.fingerprint) ? 'duplicate-row' : ''}
                >
                  <td className="date-cell">{formatDate(tx.date)}</td>
                  <td className="desc-cell" title={tx.description}>{tx.description}</td>
                  <td className={`amount-cell ${tx.type}`}>
                    {formatAmount(tx.amount, tx.type)}
                  </td>
                  <td className="status-cell">
                    {preview.duplicateFingerprints.has(tx.fingerprint) ? (
                      <span className="status-badge duplicate">{t('import_page.preview.badge_skip')}</span>
                    ) : (
                      <span className="status-badge new">{t('import_page.preview.badge_new')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {preview.transactions.length > 10 && (
          <p className="more-transactions">
            {t('import_page.preview.more_transactions', { count: preview.transactions.length - 10 })}
          </p>
        )}
      </Card>

      {preview.errors.length > 0 && (
        <Card className="errors-card">
          <CardHeader title={t('import_page.preview.errors')} />
          <ul className="error-list">
            {preview.errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </Card>
      )}

      {preview.warnings.length > 0 && (
        <Card className="warnings-card">
          <CardHeader title={t('import_page.preview.warnings')} />
          <ul className="warning-list">
            {preview.warnings.map((warn, i) => (
              <li key={i}>{warn}</li>
            ))}
          </ul>
        </Card>
      )}

      <div className="step-actions">
        <Button variant="ghost" onClick={onBack} disabled={isLoading}>
          {t('import_page.preview.start_over')}
        </Button>
        {preview.format === 'csv' && !preview.is1MoneyFormat && (
          <Button variant="secondary" onClick={onNext} disabled={isLoading}>
            {t('import_page.preview.edit_mapping')}
          </Button>
        )}
        {preview.isNinjaCashBackup ? (
          <Button
            variant="primary"
            onClick={onConfirmImport}
            disabled={isLoading}
          >
            {isLoading ? t('import_page.preview.restoring') : t('import_page.preview.restore')}
          </Button>
        ) : preview.is1MoneyFormat ? (
          <Button
            variant="primary"
            onClick={onConfirmImport}
            disabled={isLoading}
          >
            {isLoading ? t('import_page.preview.importing') : t('import_page.preview.import_1money')}
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={onConfirmImport}
            disabled={!selectedAccountId || preview.newCount === 0 || isLoading}
          >
            {isLoading ? t('import_page.preview.importing') : t('import_page.preview.import_n', { count: preview.newCount })}
          </Button>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Column Mapping Step (for CSV)
// ============================================================

interface ColumnMappingStepProps {
  headers: string[];
  mapping: ColumnMapping;
  onMappingChange: (mapping: ColumnMapping) => void;
  onBack: () => void;
  onApply: () => void;
}

function ColumnMappingStep({
  headers,
  mapping,
  onMappingChange,
  onBack,
  onApply,
}: ColumnMappingStepProps) {
  const { t } = useLanguage();
  const updateMapping = (field: keyof ColumnMapping, value: string | null) => {
    onMappingChange({ ...mapping, [field]: value || null });
  };

  const requiredFields: Array<{ key: keyof ColumnMapping; label: string }> = [
    { key: 'date', label: t('import_page.mapping.col_date') },
    { key: 'amount', label: t('import_page.mapping.col_amount') },
    { key: 'description', label: t('import_page.mapping.col_description') },
  ];

  const optionalFields: Array<{ key: keyof ColumnMapping; label: string }> = [
    { key: 'category', label: t('import_page.mapping.col_category') },
    { key: 'debit', label: t('import_page.mapping.col_debit') },
    { key: 'credit', label: t('import_page.mapping.col_credit') },
    { key: 'checkNumber', label: t('import_page.mapping.col_check') },
  ];

  return (
    <div className="mapping-step">
      <Card className="mapping-card">
        <CardHeader
          title={t('import_page.mapping.title')}
          subtitle={t('import_page.mapping.subtitle')}
        />

        <div className="mapping-grid">
          {requiredFields.map(({ key, label }) => (
            <div key={key} className="mapping-row">
              <label className="mapping-label required">{label}</label>
              <select
                className="mapping-select"
                value={mapping[key] || ''}
                onChange={(e) => updateMapping(key, e.target.value)}
              >
                <option value="">{t('import_page.mapping.select_column')}</option>
                {headers.map(h => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
            </div>
          ))}

          <div className="mapping-divider">{t('import_page.mapping.optional_fields')}</div>

          {optionalFields.map(({ key, label }) => (
            <div key={key} className="mapping-row">
              <label className="mapping-label">{label}</label>
              <select
                className="mapping-select"
                value={mapping[key] || ''}
                onChange={(e) => updateMapping(key, e.target.value)}
              >
                <option value="">{t('import_page.mapping.none_option')}</option>
                {headers.map(h => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
            </div>
          ))}
        </div>

        <div className="bank-preset-selector">
          <h4>{t('import_page.mapping.preset_hint')}</h4>
          <select
            className="preset-select"
            value=""
            onChange={(e) => {
              const preset = BANK_PRESETS.find(p => p.id === e.target.value);
              if (preset) {
                onMappingChange(preset.columnMapping);
              }
            }}
          >
            <option value="">{t('import_page.mapping.choose_bank')}</option>
            {BANK_PRESETS.map(preset => (
              <option key={preset.id} value={preset.id}>
                {preset.name} ({preset.country})
              </option>
            ))}
          </select>
        </div>
      </Card>

      <div className="step-actions">
        <Button variant="ghost" onClick={onBack}>
          {t('import_page.mapping.back')}
        </Button>
        <Button variant="primary" onClick={onApply}>
          {t('import_page.mapping.apply')}
        </Button>
      </div>
    </div>
  );
}

// ============================================================
// Result Step - With Celebration!
// ============================================================

interface ResultStepProps {
  result: UniversalImportResult;
  onStartOver: () => void;
}

function ResultStep({ result, onStartOver }: ResultStepProps) {
  const { t } = useLanguage();
  const [showCelebration, setShowCelebration] = useState(false);

  // Trigger celebration animation on success
  useEffect(() => {
    if (result.success && result.imported > 0) {
      setShowCelebration(true);
      const timer = setTimeout(() => setShowCelebration(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [result]);

  return (
    <div className="result-step">
      <Card className={`result-card ${result.success ? 'success' : 'error'} ${showCelebration ? 'celebrating' : ''}`}>
        <div className="result-icon">
          {result.success ? <SuccessIcon /> : <ErrorIcon />}
        </div>

        <h2 className="result-title">
          {result.success ? t('import_page.result.complete') : t('import_page.result.failed')}
        </h2>

        {result.success && (
          <div className="result-stats">
            <div className="result-stat">
              <span className="result-stat-value">{result.imported}</span>
              <span className="result-stat-label">{t('import_page.result.imported')}</span>
            </div>
            {result.duplicatesSkipped > 0 && (
              <div className="result-stat skipped">
                <span className="result-stat-value">{result.duplicatesSkipped}</span>
                <span className="result-stat-label">{t('import_page.result.skipped')}</span>
              </div>
            )}
            {result.newCategories.length > 0 && (
              <div className="result-stat">
                <span className="result-stat-value">{result.newCategories.length}</span>
                <span className="result-stat-label">{t('import_page.result.new_categories')}</span>
              </div>
            )}
          </div>
        )}

        {result.errors.length > 0 && (
          <div className="result-errors">
            <h4>{t('import_page.preview.errors')}</h4>
            <ul>
              {result.errors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        {result.warnings.length > 0 && (
          <div className="result-warnings">
            <h4>{t('import_page.preview.warnings')}</h4>
            <ul>
              {result.warnings.map((warn, i) => (
                <li key={i}>{warn}</li>
              ))}
            </ul>
          </div>
        )}

        {!result.success && (
          <div className="import-report-card">
            <h4>{t('import_page.result.report_title')}</h4>
            <p>
              {t('import_page.result.report_body')}
            </p>
            <a
              className="import-report-link"
              href={IMPORT_ISSUES_URL}
              target="_blank"
              rel="noreferrer"
            >
              {t('import_page.result.report_link')}
            </a>
          </div>
        )}
      </Card>

      <div className="step-actions center">
        <Button variant="primary" onClick={onStartOver}>
          {t('import_page.result.import_another')}
        </Button>
      </div>
    </div>
  );
}

// ============================================================
// Main Page Component
// ============================================================

export default function ImportDataPage() {
  const [step, setStep] = useState<ImportStep>('upload');
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [result, setResult] = useState<UniversalImportResult | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [columnMapping, setColumnMapping] = useState<ColumnMapping | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);

  const { accounts } = useAccounts();

  // Reset state when starting over
  const handleStartOver = useCallback(() => {
    setStep('upload');
    setPreview(null);
    setResult(null);
    setSelectedAccountId('');
    setColumnMapping(null);
    setCsvHeaders([]);
  }, []);

  // IMP-007: Maximum file size (10MB) to prevent browser memory crash
  const MAX_FILE_SIZE = 10 * 1024 * 1024;

  const trackFailure = useCallback((failure: UniversalImportResult, context: {
    format?: string;
    stage: ImportFailureStage;
    fileSize?: number;
  }) => {
    void trackImportFailed(buildImportFailureSummary({
      result: failure,
      format: context.format,
      stage: context.stage,
      fileSize: context.fileSize,
    }));
  }, []);

  const handleFileReject = useCallback((rejection: FileRejection) => {
    const file = rejection.file;
    const message = rejection.errors[0]?.message || i18nT('import_page.upload.reject_fallback');
    const failure = createFailedImportResult([message]);
    setResult(failure);
    setPreview(null);
    trackFailure(failure, {
      format: file?.name?.split('.').pop()?.toLowerCase() || 'unknown',
      stage: 'file_rejected',
      fileSize: file?.size,
    });
    setStep('result');
  }, [trackFailure]);

  // Handle file selection
  const handleFileSelect = useCallback(async (file: File) => {
    // IMP-007 FIX: Reject oversized files before loading into memory
    if (file.size > MAX_FILE_SIZE) {
      const failure = createFailedImportResult([
        `File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum size is 10 MB.`,
      ]);
      setResult(failure);
      setPreview(null);
      trackFailure(failure, {
        format: file.name.split('.').pop()?.toLowerCase() || 'unknown',
        stage: 'file_size',
        fileSize: file.size,
      });
      setStep('result');
      return;
    }

    setIsLoading(true);

    try {
      const previewResult = await parseFileForPreview(file);
      setPreview(previewResult);

      if (previewResult.errors.length > 0 && previewResult.transactions.length === 0) {
        const failure = createFailedImportResult(previewResult.errors, previewResult.warnings);
        setResult(failure);
        trackFailure(failure, {
          format: previewResult.format,
          stage: 'preview_validation',
          fileSize: file.size,
        });
        setStep('result');
        setIsLoading(false);
        return;
      }

      // For CSV, extract headers for column mapping
      if (previewResult.format === 'csv' && previewResult.columnMapping) {
        const content = await file.text();
        const headerLine = content.split('\n')[0] || '';
        const headers = headerLine.split(',').map(h => h.replace(/^\uFEFF/, '').replace(/"/g, '').trim());
        setCsvHeaders(headers);
        setColumnMapping(previewResult.columnMapping);
      }

      // Auto-select first account if only one exists
      if (accounts.length === 1) {
        setSelectedAccountId(accounts[0].id!);
      }

      setStep('preview');
    } catch (error) {
      console.error('[Import] File parse error:', error);
      const failure = createFailedImportResult([
        error instanceof Error ? error.message : 'An unexpected error occurred while reading the file',
      ]);
      setResult(failure);
      setPreview(null);
      trackFailure(failure, {
        format: file.name.split('.').pop()?.toLowerCase() || 'unknown',
        stage: 'parse_preview',
        fileSize: file.size,
      });
      setStep('result');
    } finally {
      setIsLoading(false);
    }
  }, [accounts, trackFailure]);

  // Handle import confirmation
  const handleConfirmImport = useCallback(async () => {
    if (!preview) return;
    
    // For regular imports (not backup, not 1Money), require account selection
    if (!preview.isNinjaCashBackup && !preview.is1MoneyFormat && !selectedAccountId) return;

    setIsLoading(true);

    try {
      let importResult: UniversalImportResult;
      
      if (preview.isNinjaCashBackup && preview.backupData) {
        // Full NinjaCash backup restore
        importResult = await importNinjaCashBackup(preview.backupData);
      } else if (preview.is1MoneyFormat && preview.rawFileContent) {
        // 1Money CSV import - uses specialized importer
        const file = new File([preview.rawFileContent], preview.fileName, { type: 'text/csv' });
        const oneMoneyResult: OneMoneyImportResult = await importOneMoneyCSV(file);
        
        // Convert 1Money result to UniversalImportResult format
        importResult = {
          success: oneMoneyResult.success,
          imported: oneMoneyResult.imported,
          skipped: oneMoneyResult.skipped,
          duplicatesSkipped: 0,
          errors: oneMoneyResult.errors,
          warnings: [
            ...oneMoneyResult.warnings,
            ...(oneMoneyResult.errorFixCount > 0 
              ? [`${oneMoneyResult.errorFixCount} error-fix transactions detected (${oneMoneyResult.errorFixTotal.toFixed(2)} total)`] 
              : []),
            ...(oneMoneyResult.recurringDetected > 0 
              ? [`${oneMoneyResult.recurringDetected} recurring patterns detected`] 
              : []),
            ...(oneMoneyResult.balancesApplied 
              ? ['Footer balances applied to accounts'] 
              : []),
          ],
          newAccounts: oneMoneyResult.newAccounts,
          newCategories: oneMoneyResult.newCategories,
        };
      } else {
        // Regular transaction import to selected account
        importResult = await importTransactions(preview, {
          accountId: selectedAccountId,
          skipDuplicates: true,
          importSource: `${preview.format}-import`,
        });
      }

      setResult(importResult);
      if (importResult.success) {
        void trackImportCompleted({
          success: importResult.success,
          imported: importResult.imported,
          skipped: importResult.skipped,
          duplicatesSkipped: importResult.duplicatesSkipped,
          newAccounts: importResult.newAccounts.length,
          newCategories: importResult.newCategories.length,
        });
      } else {
        trackFailure(importResult, {
          format: preview.format,
          stage: 'confirm_import',
          fileSize: preview.fileSize,
        });
      }
      setStep('result');
    } catch (error) {
      console.error('[Import] Import error:', error);
      const failure = createFailedImportResult([
        error instanceof Error ? error.message : 'An unexpected error occurred',
      ]);
      setResult(failure);
      trackFailure(failure, {
        format: preview.format,
        stage: 'confirm_import',
        fileSize: preview.fileSize,
      });
      setStep('result');
    } finally {
      setIsLoading(false);
    }
  }, [preview, selectedAccountId, trackFailure]);

  // Handle column mapping apply
  const handleApplyMapping = useCallback(async () => {
    if (!preview || !columnMapping) return;
    // Re-parse with new mapping would go here
    // For now, just go back to preview
    setStep('preview');
  }, [preview, columnMapping]);

  return (
    <div className="import-data-page">
      <header className="page-header">
        <h1 className="page-title">{i18nT('import_page.title')}</h1>
        <p className="page-subtitle">
          {i18nT('import_page.subtitle')}
        </p>
      </header>

      <StepIndicator currentStep={step} format={preview?.format || null} />

      <div className="import-content">
        {step === 'upload' && (
          <FileUploadStep
            onFileSelect={handleFileSelect}
            onFileReject={handleFileReject}
            isLoading={isLoading}
          />
        )}

        {step === 'preview' && preview && (
          <PreviewStep
            preview={preview}
            selectedAccountId={selectedAccountId}
            onAccountChange={setSelectedAccountId}
            onBack={handleStartOver}
            onNext={() => setStep('mapping')}
            onConfirmImport={handleConfirmImport}
            accounts={accounts.filter(a => a.isActive).map(a => ({
              id: a.id!,
              name: a.name,
              fullName: a.fullName,
            }))}
            isLoading={isLoading}
          />
        )}

        {step === 'mapping' && preview && columnMapping && (
          <ColumnMappingStep
            headers={csvHeaders}
            mapping={columnMapping}
            onMappingChange={setColumnMapping}
            onBack={() => setStep('preview')}
            onApply={handleApplyMapping}
          />
        )}

        {step === 'result' && result && (
          <ResultStep
            result={result}
            onStartOver={handleStartOver}
          />
        )}
      </div>
    </div>
  );
}
